import {
  Box,
  Flex,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { CiMap } from 'react-icons/ci';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { RiDeleteBinLine } from 'react-icons/ri';
import { IoMdDownload } from 'react-icons/io';
import { FaRegEdit, FaRegFile } from 'react-icons/fa';
import TablePagination from '../TablePagination/TablePagination';
import AddNewFormButton from './AddNewFormButton';
import axios from 'axios';
import global_download from './DownloadFIle';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import EditDocumentModal from './EditDocumentModal';
import MapFormModal from './MapFormModal';
import useTable from '../TablePagination/UseTable';

type FormDataType = {
  documentid: number;
  name: string;
  description: string;
  fileName: string;
  file: {};
};

function PayerForms({
  setTabIndex,
  setDocument,
}: {
  setTabIndex: any;
  setDocument: any;
}) {
  const [formsData, setFormsData] = useState<FormDataType[]>([]);
  const [deleteDocID, setDeleteDocID] = useState<number>(0);
  const [editDocumentID, setEditDocumentID] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [claimsPerPage, setClaimsPerPage] = useState<number>(10);
  const lastIndex = currentPage * claimsPerPage;
  const firstIndex = lastIndex - claimsPerPage;
  const toast = useToast();

  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();

  const {
    isOpen: isOpenMapForm,
    onOpen: onOpenMapForm,
    onClose: onCloseMapForm,
  } = useDisclosure();

  const downloadFile = async (id: number, marked: boolean) => {
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;
    try {
      // Fetch the token
      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const response = await axios.get(
          baseurl +
            '/App/api/get_form_by_id' +
            '?isui=false&id=' +
            id +
            '&markfields=' +
            marked,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.data.token}`,
            },
          }
        );
        global_download(
          response.data.result.file,
          response.data.result.name,
          marked
        );

        toast({
          title: 'Success',
          position: 'top',
          description: marked
            ? 'Document Downloaded with Marked Filed!'
            : 'Document Downloaded!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const deleteDocument = async (documentId: number) => {
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;

    try {
      const tokenResponse = await axios.get(tokenUrl);
      const userdetails = JSON.parse(localStorage.getItem('userdetails') || '');
      let portalid = userdetails[0].portal_Id!;

      if (tokenResponse.data.success) {
        const response = await axios.get(baseurl + '/App/api/delete_document', {
          headers: {
            Authorization: `Bearer ${tokenResponse.data.token}`,
          },
          params: {
            documentid: documentId,
            user: portalid,
          },
        });
        toast({
          title: 'Success',
          position: 'top',
          description: 'Document Successfully Deleted!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  useEffect(() => {
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;
    // Function to fetch the token and then the forms
    const fetchForms = async () => {
      try {
        const tokenResponse = await axios.get(tokenUrl);
        if (tokenResponse.data.success) {
          const formsResponse = await axios.get(
            baseurl + '/App/api/get_forms',
            {
              headers: {
                Pragma: 'no-cache',
                Authorization: 'Bearer ' + tokenResponse.data.token,
              },
            }
          );
          if (formsResponse.data.success) {
            const filteredData = formsResponse.data.forms.filter((item: any) =>
              [item.name, item.description].some(
                value =>
                  value &&
                  value.toLowerCase().includes(searchQuery.toLowerCase())
              )
            );
            setFormsData(filteredData);
            // setFormsData(formsResponse.data.forms);
          } else {
            throw new Error('Failed to fetch forms');
          }
        } else {
          throw new Error('Failed to retrieve auth token');
        }
      } catch (error) {
        console.error('Error fetching forms:', error);
      }
    };

    fetchForms();
  }, [count, searchQuery]);

  const { slice, range } = useTable(
    formsData.length > 0 ? formsData : formsData,
    currentPage,
    claimsPerPage
  );

  return (
    <>
      <Box display=" flex" flexDirection="column">
        <Flex display="flex" justifyContent="flex-end" paddingBottom=".5rem">
          <Flex flexDirection="row" float="right">
            <Flex mt="17px">
              <AddNewFormButton count={count} setCount={setCount} />
            </Flex>
            <Input
              marginBottom="5px"
              marginTop="15px"
              htmlSize={24}
              width="auto"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              marginLeft="auto" // Add this line
            />
          </Flex>
        </Flex>
        <TableContainer borderRadius="5px" borderTopColor="black">
          <Table
            variant="striped"
            colorScheme="blackAlpha"
            size="sm"
            overflow="Auto"
          >
            <Thead bgColor="#002677">
              <Tr>
                <Th color="white">NAME</Th>
                <Th color="white">DESCRIPTION</Th>
                <Th color="white" isNumeric>
                  ACTIONS
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              <>
                {formsData
                  ? formsData.slice(firstIndex, lastIndex).map(data => (
                      <Tr key={data.documentid}>
                        <Td color="blue">
                          <Link
                            onClick={() => {
                              setEditDocumentID(data.documentid);
                              onOpenEdit();
                            }}
                          >
                            {data.name}
                          </Link>
                        </Td>
                        <Td>{data.description}</Td>
                        <Td pt="0" pb="0" mt="0" mb="0" isNumeric pr="30px">
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<BsThreeDotsVertical />}
                              size="xs"
                              aria-label="Options"
                              variant="outline"
                            />
                            <Portal>
                              <MenuList>
                                <MenuItem
                                  icon={<IoMdDownload />}
                                  onClick={() => {
                                    downloadFile(data.documentid, false);
                                  }}
                                >
                                  Download
                                </MenuItem>
                                <MenuItem
                                  icon={<IoMdDownload />}
                                  onClick={() => {
                                    downloadFile(data.documentid, true);
                                  }}
                                >
                                  Marked Fields Download
                                </MenuItem>

                                <MenuItem
                                  icon={<FaRegEdit />}
                                  onClick={() => {
                                    onOpenEdit();
                                    setEditDocumentID(data.documentid);
                                  }}
                                >
                                  Edit
                                </MenuItem>
                                <MenuItem
                                  icon={<CiMap />}
                                  onClick={() => {
                                    onOpenMapForm();
                                    setEditDocumentID(data.documentid);
                                  }}
                                >
                                  Map Form Fields
                                </MenuItem>
                                <MenuItem
                                  icon={<FaRegFile />}
                                  onClick={() => {
                                    setTabIndex(1);
                                    setDocument(data);
                                  }}
                                >
                                  Variations
                                </MenuItem>
                                <MenuItem
                                  icon={<RiDeleteBinLine />}
                                  onClick={() => {
                                    setDeleteDocID(data.documentid);
                                    onOpenDelete();
                                  }}
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Portal>
                          </Menu>
                        </Td>
                      </Tr>
                    ))
                  : ''}
              </>
            </Tbody>
          </Table>
          <Flex display="flex" flexDirection="row">
            <Flex />
            <Spacer />
            <TablePagination
              range={range}
              slice={slice}
              setPage={setCurrentPage}
              page={currentPage}
            />
            <Spacer />
            <Select
              value={claimsPerPage}
              onChange={e => setClaimsPerPage(parseInt(e.target.value))}
              width="100px"
              height="30px"
              mt="5px"
              mr="5px"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </Flex>
        </TableContainer>
      </Box>
      <ConfirmDeleteModal
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        deleteFunction={deleteDocument}
        docID={deleteDocID}
        count={count}
        setCount={setCount}
      />
      <EditDocumentModal
        isOpen={isOpenEdit}
        onClose={onCloseEdit}
        id={editDocumentID}
        count={count}
        setCount={setCount}
      />
      <MapFormModal
        isOpen={isOpenMapForm}
        onClose={onCloseMapForm}
        documentID={editDocumentID}
        count={count}
        setCount={setCount}
        downloadFile={downloadFile}
      />
    </>
  );
}

export default PayerForms;
